import React from 'react';

import { AnalyzedSection, Status } from '@/components/admin/PrintTextComparison/types';
import cn from '@/utils/cn';

const NAV = 'tw-sticky tw-top-4 tw-max-h-[calc(100vh-2rem)] tw-w-64 tw-flex-shrink-0 '
  + 'tw-self-start tw-overflow-y-auto';
// Override the app's global <button> chrome (grey background + all-sides border) so these read as a
// clean list: transparent background, and only a left accent border (top/right/bottom zeroed).
const ITEM_BASE = 'tw-box-border tw-flex tw-w-full tw-bg-transparent tw-border-solid '
  + 'tw-border-t-0 tw-border-r-0 tw-border-b-0 tw-border-l-2 tw-py-2.5 tw-pl-3 tw-pr-2 '
  + 'tw-text-left tw-transition-colors';
const ITEM_SELECTED = 'tw-border-l-spa-accent';
const ITEM_DEFAULT = 'tw-border-l-transparent hover:tw-border-l-spa-border-hover';
const DOT = 'tw-h-2 tw-w-2 tw-flex-shrink-0 tw-rounded-full';

// Filled success = matches print; filled warning = changed (review); filled accent = in print but
// not online yet; hollow = no counterpart or not compared.
const DOT_STYLE: Record<Status, string> = {
  changed: 'tw-bg-spa-warning',
  no_change: 'tw-bg-spa-success',
  no_print_match: 'tw-border-2 tw-border-solid tw-border-spa-border-input',
  not_compared: 'tw-border-2 tw-border-solid tw-border-spa-border-input',
  online_missing: 'tw-bg-spa-accent-solid',
};

interface Props {
  onSelect: (sectionId: number | null) => void;
  sections: AnalyzedSection[];
  selectedId: number | null | undefined;
}

// "N changes · 100%" reads as a contradiction, so when rounding lands on 100 despite changes,
// show >99% instead.
const matchPct = (ratio: number, changeCount: number) => {
  const rounded = Math.round(ratio * 100);
  return rounded === 100 && changeCount > 0 ? '>99%' : `${rounded}%`;
};

// Compact per-section summary under the title: change count and/or % match, so the list is scannable
// (and doubles as the comparison overview). Relocated entries aren't real changes, so exclude them.
const summarize = (section: AnalyzedSection) => {
  if (section.status === 'no_print_match') return 'No print match';
  if (section.status === 'not_compared') return 'Not compared';
  if (section.status === 'online_missing') return 'Not online yet — draft ready';

  const count = section.changes.filter(change => !change.relocated).length;
  const changes = count === 0 ? 'No changes' : `${count} change${count === 1 ? '' : 's'}`;
  const pct = section.ratio === null ? null : matchPct(section.ratio, count);
  return pct ? `${changes} · ${pct}` : changes;
};

const SectionList = ({ onSelect, sections, selectedId }: Props) => (
  <nav aria-label="Sections" className={NAV}>
    {sections.map((section) => {
      const selected = section.sectionId === selectedId;
      return (
        <button
          key={section.sectionId ?? 'opener'}
          aria-current={selected ? 'true' : undefined}
          className={cn(ITEM_BASE, selected ? ITEM_SELECTED : ITEM_DEFAULT)}
          type="button"
          onClick={() => onSelect(section.sectionId)}
        >
          <span aria-hidden="true" className={cn(DOT, 'tw-mt-1.5', DOT_STYLE[section.status])} />
          <span className="tw-ml-2 tw-min-w-0">
            <span
              className={cn(
                'tw-block tw-text-sm',
                selected ? 'tw-font-semibold tw-text-spa-accent-text' : 'tw-font-medium tw-text-spa-heading',
              )}
            >
              {section.title}
            </span>
            <span className="tw-mt-0.5 tw-block tw-text-xs tw-tabular-nums tw-text-spa-secondary">
              {summarize(section)}
            </span>
          </span>
        </button>
      );
    })}
  </nav>
);

export default SectionList;
